import React, { FC } from 'react';
import {
  Channel,
  ChannelConfiguration,
  Configuration,
} from 'api/notifications';
import { SimpleConfiguration } from 'api/notifications/configuration';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import {
  NotificationUserType,
  useEventTypeMessage,
  useNotificationContext,
} from '../../hooks';
import { ChannelOption, ChannelOptions, Item, Title } from '../basics';
import { useChannels, empty, ChannelDisabled } from '../channels';

export interface SimpleConfigurationProps {
  configuration: SimpleConfiguration;
  mandatory?: boolean;
  mandatoryConfig?: ChannelConfiguration;
  onChange: (configuration: Configuration) => void;
}

export const SimpleConfigurationView: FC<SimpleConfigurationProps> = ({
  configuration,
  mandatory = false,
  mandatoryConfig,
  onChange,
}) => {
  const channelEnabledMap = useChannels();
  const channels = Object.keys(channelEnabledMap) as Channel[];
  const { isCustomerPortal } = useNotificationContext();
  const { hasAccessToResource } = useWithAccess();
  const isDisabled =
    isCustomerPortal &&
    !hasAccessToResource({
      rule: 'SETTINGS_CUSTOMER_PORTAL:WRITE',
    });

  return (
    <Item>
      <Title>
        {useEventTypeMessage(configuration.eventType, {
          userType: isCustomerPortal
            ? NotificationUserType.Customer
            : NotificationUserType.Vendor,
        })}
      </Title>
      <ChannelOptions
        value={Object.entries(configuration.channels)
          .filter(([, value]) => !!value)
          .map(([key]) => key)}
        onChange={(values) =>
          onChange({
            ...configuration,
            channels: (values as any as Channel[]).reduce(
              (prev, channel) => ({ ...prev, [channel]: true }),
              empty,
            ),
          })
        }
      >
        {channels.map((x) => (
          <ChannelOption
            key={x}
            value={x}
            disabled={
              isDisabled ||
              (mandatoryConfig ? mandatoryConfig[x] : mandatory) ||
              (!channelEnabledMap[x].enabled && !isCustomerPortal)
            }
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(channelEnabledMap[x].enabled === false
              ? {
                  title: (channelEnabledMap[x] as ChannelDisabled)
                    .disabledMessage,
                }
              : {})}
          />
        ))}
      </ChannelOptions>
    </Item>
  );
};
